import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { printService } from 'api/services/printService';
import { apiConfig } from 'api/config/apiConfig';
import { tokenManager } from 'auth/tokenManager';
import type { PairingCodeCreated, PrintAgent } from 'api/types';

/**
 * Pair a new Print Agent, or a machine that replaces one (new PC, reinstall).
 * Step 1: name and site. Step 2: the code, shown ONCE in large type with its
 * expiry, and the exact command to run on the agent machine. The server keeps
 * only a hash, so closing this dialog loses the code for good (BR-PRN-011).
 */
interface Props {
  isOpen: boolean;
  /** Set for "Replace agent": the new machine takes over this agent's printers. */
  replaceAgent?: PrintAgent | null;
  onClose: () => void;
  /** A code was created: refresh the open-codes count. */
  onCreated: () => void;
}

/** The API host the agent talks to: the web app's API base without its /api suffix. */
export const agentServerUrl = () => apiConfig.baseURL.replace(/\/+$/, '').replace(/\/api$/i, '');

const PairPrintAgentModal: React.FC<Props> = ({ isOpen, replaceAgent, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [site, setSite] = useState('');
  const [requireApproval, setRequireApproval] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<PairingCodeCreated | null>(null);
  const [now, setNow] = useState(Date.now());
  /** Expiry on this browser's clock: the code's own lifetime (server createdAt to expiresAt) from now, so a skewed server clock does not shorten it. */
  const [expiresLocal, setExpiresLocal] = useState(0);
  const [closeConfirm, setCloseConfirm] = useState(false);

  useEffect(() => {
    // Closed: forget the code (shown once, BR-PRN-011) and stop the countdown.
    if (!isOpen) { setCreated(null); setCloseConfirm(false); return; }
    setName(replaceAgent ? replaceAgent.agentName : '');
    setSite(replaceAgent?.siteName ?? '');
    setRequireApproval(true);
    setFieldError(null); setServerError(null); setCreated(null); setBusy(false);
  }, [isOpen, replaceAgent]);

  useEffect(() => {
    if (!created || !isOpen) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [created, isOpen]);

  const left = created ? Math.max(0, Math.floor((expiresLocal - now) / 1000)) : 0;
  const expired = !!created && left === 0;
  const command = useMemo(() => created
    ? `crs-print-agent.exe pair --server ${agentServerUrl()} --tenant ${tokenManager.getTenantId() ?? '<tenant id>'} --app ${tokenManager.getAppId() ?? '<app id>'} --code ${created.code}`
    : '', [created]);

  const create = useCallback(async () => {
    const n = name.trim();
    if (n.length < 2 || n.length > 100) { setFieldError('The name is 2 to 100 characters, e.g. Kitchen PC.'); return; }
    setBusy(true); setServerError(null);
    try {
      const c = await printService.createPairingCode({ agentName: n, siteName: site.trim() || undefined, replaceAgentId: replaceAgent?.id, requireApproval });
      const t0 = Date.now();
      // The open-codes list carries createdAt; expiresAt - createdAt is the lifetime whatever the server clock says.
      let lifetime = new Date(c.expiresAt).getTime() - t0;
      try {
        const listed = (await printService.getPairingCodes()).find((x) => x.id === c.id);
        if (listed?.createdAt) lifetime = new Date(listed.expiresAt).getTime() - new Date(listed.createdAt).getTime();
      } catch { /* fall back to the browser clock */ }
      setExpiresLocal(t0 + lifetime);
      setCreated(c); setNow(Date.now());
      onCreated();
    } catch (err: any) {
      setServerError(err?.response?.data?.message ?? 'The pairing code could not be created.');
    } finally { setBusy(false); }
  }, [name, site, replaceAgent, requireApproval, onCreated]);

  const copy = useCallback(async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); notificationService.toast.success(`${what} copied.`); }
    catch { notificationService.toast.warning('Copy blocked by the browser. Select the text and copy it.'); }
  }, []);

  const requestClose = () => { if (created && !expired) setCloseConfirm(true); else onClose(); };

  if (!isOpen) return null;
  const mm = String(Math.floor(left / 60)).padStart(2, '0');
  const ss = String(left % 60).padStart(2, '0');

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" role="dialog" aria-modal="true" style={{ minHeight: 'auto', maxWidth: 640 }} data-pair-modal>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{replaceAgent ? `Replace agent — ${replaceAgent.agentName}` : 'Pair a new Print Agent'}</h2>
          <button className="modal-close" onClick={requestClose} disabled={busy} aria-label="Close"><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          {!created && (
            <>
              <p style={{ marginTop: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {replaceAgent
                  ? `The new machine takes over the printers of ${replaceAgent.agentName}. The old one stops getting jobs once the new one is active.`
                  : 'Install the Print Agent on a PC on the same network as the printers, then run the command shown in the next step. The code works once, for 15 minutes.'}
              </p>
              {serverError && <div className="form-error-message" style={{ marginBottom: 10 }}>{serverError}</div>}
              <div className="form-grid-2">
                <div className="form-group" data-field="agentName">
                  <label className="form-label" htmlFor="pair-name">Agent name <span className="text-danger">*</span></label>
                  <input id="pair-name" className={`form-input${fieldError ? ' form-field-error' : ''}`} value={name} maxLength={100} autoFocus
                    placeholder="e.g., Kitchen PC" onChange={(e) => { setFieldError(null); setName(e.target.value); }} />
                  {fieldError && <div className="form-error-message">{fieldError}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="pair-site">Site</label>
                  <input id="pair-site" className="form-input" value={site} maxLength={100} placeholder="e.g., Ground floor kitchen"
                    onChange={(e) => setSite(e.target.value)} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <input type="checkbox" checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} />
                <span>
                  Require approval before it prints
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
                    An agent without a TPM chip always waits for approval, whatever is ticked here.
                  </span>
                </span>
              </label>
            </>
          )}
          {created && (
            <div data-pair-code-shown>
              <p style={{ marginTop: 0, fontSize: '0.875rem' }}>
                Pairing code for <strong>{created.agentName}</strong>. It is shown <strong>only now</strong> — write it down or copy the command.
              </p>
              <div className="prn-code" data-pair-code>{created.code}</div>
              <p style={{ textAlign: 'center', margin: '0 0 12px', fontWeight: 600, color: expired ? 'var(--danger, #dc2626)' : 'var(--text-secondary)' }} data-pair-expiry>
                {expired ? 'Expired. Close and create a new code.' : `Expires in ${mm}:${ss}`}
              </p>
              <label className="form-label">On the agent machine, run:</label>
              <div className="prn-cmd" data-pair-command>{command}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => void copy(command, 'Command')}>Copy command</button>
                <button type="button" className="btn btn-secondary" onClick={() => void copy(created.code, 'Code')}>Copy code</button>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>
                The agent appears on Print Agents once it has paired. {requireApproval ? 'Approve it there before it prints.' : ''}
              </p>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {!created ? (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void create()} disabled={busy}>{busy ? 'Creating…' : 'Create pairing code'}</button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={requestClose}>Done</button>
          )}
        </div>
      </div>
      <ConfirmDialog open={closeConfirm} title="Close the pairing code?"
        message="The code is not shown again. If the agent is not paired yet you will need a new code. It stays valid until it expires or is withdrawn."
        confirmLabel="Close" cancelLabel="Keep it open" onConfirm={() => { setCloseConfirm(false); onClose(); }} onCancel={() => setCloseConfirm(false)} />
    </>
  );
};

export default PairPrintAgentModal;
